// src/utils/notify.js
//
// Messages for the bell in the header (components/Header.js). No email: the
// person sees them the next time they open the website.
//
// Used for team and dispatcher changes, so the dispatchers and Main Admins
// affected hear about a change instead of finding a driver or a load gone.

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { findTeamById, isOnTeam } from './teams';

const rolesOf = (user) => (Array.isArray(user?.role) ? user.role : [user?.role].filter(Boolean));
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Send each { userId, message } once. Nobody is told about their own change
 * (`actorId`), and empty ids are skipped. Failures are logged, never thrown:
 * a missed message must not undo or block the change it describes.
 */
export async function notifyUsers({ tenantId, actorId = null, notes = [], extra = {} }) {
  const seen = new Set();
  const list = notes.filter(n => {
    if (!n?.userId || !n.message || n.userId === actorId) return false;
    const key = `${n.userId}|${n.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  await Promise.all(list.map(n => addDoc(collection(db, 'notifications'), {
    userId: n.userId,
    tenantId,
    type: 'admin_update',
    message: n.message,
    ...extra,
    read: false,
    createdAt: serverTimestamp(),
  }).catch(error => console.error('notify: could not send', error))));
}

/** Ids of the Main Admins on a team (role string or list). */
export function teamMainAdminIds(users = [], teamId) {
  if (!teamId) return [];
  return users
    .filter(u => u && u.active !== false && rolesOf(u).includes('Main Admin') && isOnTeam(u, teamId))
    .map(u => u.id);
}

/** "LoadMemo 1 (Test LoadMemo)", or a plain fallback when the team is unknown. */
export function teamLabel(companies = [], teamId) {
  if (!teamId) return 'no team';
  const team = findTeamById(companies, teamId);
  if (!team) return 'another team';
  return team.companyName ? `${team.name} (${team.companyName})` : team.name;
}

export const nameOf = (list = [], id) => list.find(x => x.id === id)?.name || 'someone';

const listOf = (names) => (names.length <= 1 ? names.join('')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/**
 * The messages for a dispatcher or Main Admin moved between teams (or out of a
 * company): to the person, to each team's Main Admins, and to whoever takes
 * over their drivers and loads. `handovers` are driverHandovers, and
 * `loadHandovers` are planLoadHandovers (utils/loadHandover).
 */
export function personMoveNotes({
  user, id, left = [], joined = [], access = 'view', handovers = [], loadHandovers = [],
  users = [], drivers = [], companies = [],
}) {
  const before = Array.isArray(user?.teamIds) ? user.teamIds : [];
  const newTeams = joined.filter(t => t && !before.includes(t));
  if (left.length === 0 && newTeams.length === 0) return [];

  const name = user?.name || user?.email || 'Someone';
  const asMainAdmin = rolesOf(user).includes('Main Admin');
  const fromText = left.map(t => teamLabel(companies, t)).join(', ');
  const toText = newTeams.map(t => teamLabel(companies, t)).join(', ');
  const notes = [];

  // The person.
  const theirLoads = asMainAdmin ? "the team's unfinished loads" : 'your unfinished loads';
  const accessText = left.length === 0 ? ''
    : access === 'manage' ? ` You can still see and manage ${theirLoads} from there until they are delivered.`
    : access === 'none' ? ' You no longer see its loads.'
    : ` You can still see ${theirLoads} from there until they are delivered, but not change them.`;
  notes.push({
    userId: id,
    message: (left.length && newTeams.length ? `You were moved from team ${fromText} to team ${toText}.`
      : left.length ? `You were taken off team ${fromText}.` : `You joined team ${toText}.`) + accessText,
  });

  // Main Admins of each team left: who left, and where their work went.
  left.forEach(teamId => {
    const lines = [`${name} left your team ${teamLabel(companies, teamId)}${newTeams.length ? ` for team ${toText}` : ''}.`];
    handovers.filter(h => drivers.find(d => d.id === h.driverId)?.teamId === teamId).forEach(h => {
      const driverName = nameOf(drivers, h.driverId);
      lines.push(h.dispatcherId ? `Driver ${driverName} now goes to ${nameOf(users, h.dispatcherId)}.`
        : `Driver ${driverName} has no dispatcher now. Choose one.`);
    });
    const teamLoads = loadHandovers.filter(h => h.teamId === teamId);
    const unassigned = teamLoads.filter(h => !h.toDispatcherId);
    if (teamLoads.length > unassigned.length) {
      lines.push(`${plural(teamLoads.length - unassigned.length, 'current load was', 'current loads were')} handed to their drivers' dispatchers.`);
    }
    if (unassigned.length > 0) {
      lines.push(`${unassigned.length === 1 ? 'Load' : 'Loads'} ${listOf(unassigned.map(h => h.loadId))} ${unassigned.length === 1 ? 'has' : 'have'} no dispatcher now. Assign one.`);
    }
    teamMainAdminIds(users, teamId).filter(uid => uid !== id)
      .forEach(uid => notes.push({ userId: uid, message: lines.join(' ') }));
  });

  // Whoever takes over drivers or loads.
  const takers = new Map();
  const take = (uid) => {
    if (!takers.has(uid)) takers.set(uid, { drivers: [], loads: [] });
    return takers.get(uid);
  };
  handovers.filter(h => h.dispatcherId).forEach(h => take(h.dispatcherId).drivers.push(nameOf(drivers, h.driverId)));
  loadHandovers.filter(h => h.toDispatcherId).forEach(h => take(h.toDispatcherId).loads.push(h.loadId));
  takers.forEach((t, uid) => {
    const parts = [];
    if (t.drivers.length) parts.push(`${t.drivers.length === 1 ? 'driver' : 'drivers'} ${listOf(t.drivers)}`);
    if (t.loads.length) parts.push(`${t.loads.length === 1 ? 'load' : 'loads'} ${listOf(t.loads)}`);
    notes.push({ userId: uid, message: `${name} left the team. You are now the dispatcher for ${parts.join(' and ')}.` });
  });

  // Main Admins of each team joined.
  newTeams.forEach(teamId => teamMainAdminIds(users, teamId).filter(uid => uid !== id)
    .forEach(uid => notes.push({ userId: uid, message: `${name} joined your team ${teamLabel(companies, teamId)}.` })));

  return notes;
}

